import React, { useMemo } from "react";
import { TrendingUp } from "lucide-react";

const WIN_STATUSES = ["Won", "Ready to Start"];
const ENGAGED_STATUSES = ["Contacted", "Consultation Scheduled", "Assessment Done", "Won", "Ready to Start"];

export const LeadSourceReport = ({ leads = [] }) => {
  const rows = useMemo(() => {
    const by = {};
    for (const l of leads) {
      const s = l.source || "Unknown";
      const r = (by[s] = by[s] || { source: s, total: 0, engaged: 0, won: 0 });
      r.total += 1;
      if (ENGAGED_STATUSES.includes(l.status)) r.engaged += 1;
      if (WIN_STATUSES.includes(l.status)) r.won += 1;
    }
    return Object.values(by).sort((a, b) => b.total - a.total);
  }, [leads]);

  const max = Math.max(1, ...rows.map((r) => r.total));
  const best = rows.filter((r) => r.won > 0).sort((a, b) => b.won / b.total - a.won / a.total)[0];

  return (
    <div data-testid="lead-source-report" className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
        <h3 className="font-display font-bold text-[#0E2A33] flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-[#2E9E6A]" /> Lead Source Report — which channel wins clients
        </h3>
        {best && (
          <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-3 py-1 font-medium">
            Best converter: {best.source} ({best.won}/{best.total} won)
          </span>
        )}
      </div>
      <p className="text-[11px] text-slate-400 mb-4">Live data from your lead pipeline. "Engaged" = contacted or further; "Won" = Won / Ready to Start.</p>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.source}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-[#0E2A33]">{r.source}</span>
              <span className="text-slate-500">
                {r.total} lead{r.total !== 1 ? "s" : ""} · {r.engaged} engaged · <span className={r.won > 0 ? "text-emerald-600 font-bold" : ""}>{r.won} won</span>
                {r.total > 0 && <span className="text-slate-400"> ({Math.round((r.won / r.total) * 100)}%)</span>}
              </span>
            </div>
            <div className="h-3 rounded-full bg-slate-100 overflow-hidden relative">
              <div className="h-full bg-[#BFE3ED] rounded-full" style={{ width: `${(r.total / max) * 100}%` }} />
              <div className="h-full bg-sky-400 rounded-full absolute top-0 left-0" style={{ width: `${(r.engaged / max) * 100}%` }} />
              <div className="h-full bg-[#2E9E6A] rounded-full absolute top-0 left-0" style={{ width: `${(r.won / max) * 100}%` }} />
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No leads yet.</p>}
      </div>
      <div className="flex items-center gap-4 mt-4">
        {[["bg-[#BFE3ED]", "Total leads"], ["bg-sky-400", "Engaged"], ["bg-[#2E9E6A]", "Won"]].map(([c, label]) => (
          <span key={label} className="flex items-center gap-1.5 text-[10px] text-slate-500">
            <span className={`h-2.5 w-2.5 rounded-full ${c} inline-block`} /> {label}
          </span>
        ))}
        <span className="text-[10px] text-slate-400 ml-auto">Tip: GA4 tracks web conversions too — analytics.google.com</span>
      </div>
    </div>
  );
};
